import { Node, mergeAttributes } from "@tiptap/core";

/**
 * WikiLink — an inline atom node for Obsidian-style [[wiki-links]].
 *
 * • In the Visual (WYSIWYG) editor it renders as a clickable brand-coloured chip.
 * • It round-trips losslessly to/from Markdown as `[[Target]]` or `[[Target|alias]]`
 *   via the official @tiptap/markdown tokenizer/parse/render hooks, so the Vault
 *   graph, backlinks and search keep working exactly as before.
 */
export const WikiLink = Node.create({
  name: "wikiLink",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      target: { default: "" },
      alias: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-wikilink]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = node.attrs.alias || node.attrs.target;
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-wikilink": node.attrs.target,
        "data-alias": node.attrs.alias || "",
        class: "wikilink-chip",
      }),
      label,
    ];
  },

  // Plain-text / copy-paste representation.
  renderText({ node }) {
    const t = node.attrs.target || "";
    const a = node.attrs.alias;
    return a ? `[[${t}|${a}]]` : `[[${t}]]`;
  },

  // ----- Markdown integration (read by @tiptap/markdown) -----
  markdownTokenName: "wikiLink",

  markdownTokenizer: {
    name: "wikiLink",
    level: "inline",
    start(src) {
      const i = src.indexOf("[[");
      return i === -1 ? undefined : i;
    },
    tokenize(src) {
      const m = /^\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/.exec(src);
      if (!m) return undefined;
      return {
        type: "wikiLink",
        raw: m[0],
        target: m[1].trim(),
        alias: m[2] ? m[2].trim() : null,
      };
    },
  },

  parseMarkdown(token) {
    return {
      type: "wikiLink",
      attrs: { target: token.target, alias: token.alias || null },
    };
  },

  renderMarkdown(node) {
    const t = node.attrs?.target || "";
    const a = node.attrs?.alias;
    return a ? `[[${t}|${a}]]` : `[[${t}]]`;
  },
});

export default WikiLink;
